import { IgTypography } from "@intelligems/ig-theme";
import { Box, Stack, useTheme } from "@mui/material";

import {
  ATLAS_SUGGESTIONS_ID,
  ATLAS_SUGGESTIONS_LABEL,
  atlasSuggestionOptionId,
} from "./atlasSuggestionIds";

interface AtlasSuggestionsProps {
  suggestions: readonly string[];
  activeIndex: number;
  /** Panel heading, also the listbox's accessible name. */
  label?: string;
  onHighlightSuggestion: (index: number) => void;
  onCommitSuggestion: (suggestion: string) => void;
}

/**
 * The suggestion dropdown that opens under the rule composer while its input
 * holds focus — a search-history pattern, so the input keeps focus throughout
 * and the highlighted row is published through `aria-activedescendant` rather
 * than by moving focus into the list. That is also why a row commits on
 * mousedown-prevented click: letting the input blur first would close the
 * panel out from under the pointer.
 */
export const AtlasSuggestions = ({
  suggestions,
  activeIndex,
  label = ATLAS_SUGGESTIONS_LABEL,
  onHighlightSuggestion,
  onCommitSuggestion,
}: AtlasSuggestionsProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        bgcolor: theme.igBackgroundColors.baseLightHigh,
        border: `1px solid ${theme.igBorders.core.default}`,
        borderRadius: "8px",
        boxShadow: theme.igShadows.low,
        left: 0,
        overflow: "hidden",
        position: "absolute",
        py: 2,
        right: 0,
        top: "calc(100% + 4px)",
        zIndex: 2,
      }}
    >
      <Box sx={{ px: 3, py: 1.5 }}>
        <IgTypography
          variant="body3"
          color="text.placeholder"
          sx={{ lineHeight: 1.5, textTransform: "uppercase" }}
        >
          {label}
        </IgTypography>
      </Box>
      <Stack
        component="ul"
        id={ATLAS_SUGGESTIONS_ID}
        role="listbox"
        aria-label={label}
        onMouseLeave={() => onHighlightSuggestion(-1)}
        tabIndex={-1}
        sx={{
          listStyle: "none",
          m: 0,
          maxHeight: 280,
          overflowY: "auto",
          px: 2,
          py: 0,
        }}
      >
        {suggestions.map((suggestion, index) => {
          const highlighted = index === activeIndex;
          return (
            <Box
              key={suggestion}
              component="li"
              id={atlasSuggestionOptionId(index)}
              role="option"
              aria-selected={highlighted}
              tabIndex={-1}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => onHighlightSuggestion(index)}
              onClick={() => onCommitSuggestion(suggestion)}
              sx={{
                bgcolor: highlighted
                  ? theme.igBackgroundColors.interactive.neutral.hover
                  : "transparent",
                borderRadius: "4px",
                color: highlighted
                  ? theme.palette.text.primary
                  : theme.palette.text.secondary,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 3,
                px: 3,
                py: 3,
              }}
            >
              <IgTypography
                variant="body1"
                color="inherit"
                sx={{
                  flex: 1,
                  lineHeight: 1.5,
                  minWidth: 0,
                  wordBreak: "break-word",
                }}
              >
                {suggestion}
              </IgTypography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};
